import PropTypes from 'prop-types';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { useMemo, useState } from 'react';
import { Icon } from '@icp/components';
import { get } from 'lodash-es';
import { FormHelperTextMaterial } from '../../FormHelperText';
import ItemField from '../SelectElement/ItemField';
import { filterOption } from '../SelectElement/utils';

function DownIcon(props) {
  return <Icon name="oct:chevron-down" size={18} {...props} />;
}

function AutoCompleteMaterial(props) {
  const {
    keyPath,
    id,
    title,
    value,
    componentProps,
    validation,
    disabled,
    readonly,
    status,
    helpers,
    onChange,
    onTouchChanged,

    searchTextColIds,
    loading,
    options,
    mapping,
    itemField,
    onSearch,
  } = props;

  const [inputValue, setInputValue] = useState('');

  const useClientSideFilter = !onSearch;

  const handleChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      onChange(newValue);
    } else if (newValue && typeof newValue === 'object') {
      // 用户从下拉列表选中了一个 option
      onChange(String(newValue[mapping.value] ?? ''));
    } else {
      onChange('');
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'reset') return;
    setInputValue(newInputValue);
    onChange(newInputValue);
    if (onSearch) {
      onSearch(newInputValue);
    }
  };

  // 找到当前 value 对应的 option 对象（如果有的话）
  const selectedOption = useMemo(() => {
    if (!value || !options?.length) return null;
    return options.find((op) => String(op[mapping.value] ?? '') === value) || null;
  }, [value, options, mapping]);

  return (
    <Autocomplete
      fullWidth={true}
      freeSolo={true}
      {...componentProps}
      id={id}
      value={selectedOption}
      inputValue={value ?? inputValue}
      disabled={disabled}
      readOnly={readonly}
      options={loading ? [] : options || []}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return String(get(option, mapping.label) ?? '');
      }}
      loading={loading}
      noOptionsText={null}
      popupIcon={<DownIcon />}
      clearIcon={<Icon name="oct:x" size={18} />}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={onTouchChanged}
      isOptionEqualToValue={(option, val) => {
        if (typeof val === 'string') {
          return String(option[mapping.value] ?? '') === val;
        }
        return option[mapping.value] === val?.[mapping.value];
      }}
      filterOptions={(ops) => {
        if (!useClientSideFilter) return ops;
        return ops.filter((option) => filterOption(value || '', option, searchTextColIds));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={title}
          required={validation?.required}
          error={status === 'error'}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          helperText={
            !helpersIsEmpty(helpers) ? <FormHelperTextMaterial helpers={helpers} /> : null
          }
        />
      )}
      renderOption={(p, option) => {
        const reactKey = option[mapping.value];
        return (
          <li {...p} key={reactKey}>
            {itemField ? (
              <ItemField keyPath={keyPath} itemField={itemField} option={option} />
            ) : (
              get(option, mapping.label)
            )}
          </li>
        );
      }}
    />
  );
}

AutoCompleteMaterial.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.string,
  componentProps: PropTypes.shape({}),
  mapping: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  searchTextColIds: PropTypes.arrayOf(PropTypes.string),
  itemField: PropTypes.shape({}),
  fieldTitleProps: PropTypes.shape({}),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
  loading: PropTypes.bool,
  onSearch: PropTypes.func,
};

export default AutoCompleteMaterial;
